import type { PublishedPack } from '@planuze/admin-contracts';
import { useMemo } from 'react';
import { usePacksCatalogContent } from '../../content/sections/packs-catalog.js';
import { FacetMultiSelect } from '../filters/facet-multi-select.js';
import { SegmentedControl, type SegmentOption } from '../filters/segmented-control.js';
import { SearchInput } from '../search-input.js';
import {
  type CatalogFilters,
  KIND_ORDER,
  TIER_ORDER,
  VISIBILITY_FILTER_ORDER,
} from './packs-catalog-filter-model.js';

type VisibilityFilter = CatalogFilters['visibility'];
type VerifiedFilter = CatalogFilters['verified'];
type ActiveFilter = CatalogFilters['active'];
type CatalogSort = CatalogFilters['sort'];

interface Props {
  /** Lista completa carregada — deriva opções de tier/tipo e contadores de visibilidade. */
  readonly packs: readonly PublishedPack[];
  readonly filters: CatalogFilters;
  readonly onChange: (patch: Partial<CatalogFilters>) => void;
  readonly resultCount: number;
  readonly totalCount: number;
}

/* Toolbar de filtros client-side do catálogo (ADR-0704): busca + visibilidade + tier + tipo +
   verificação + versão ativa + ordenação. Opções de tier/tipo derivam dos dados carregados (não
   mostra faceta que não casa nada); os contadores de visibilidade refletem a busca corrente. */
export function PacksCatalogFilters({ packs, filters, onChange, resultCount, totalCount }: Props) {
  const content = usePacksCatalogContent();
  const copy = content.filtersUi;
  const filterLabels = content.filterModel;
  const searchQuery = filters.search.trim().toLowerCase();
  const searchScoped = useMemo(
    () =>
      searchQuery.length === 0
        ? packs
        : packs.filter(
            (p) =>
              p.displayName.toLowerCase().includes(searchQuery) ||
              p.id.toLowerCase().includes(searchQuery),
          ),
    [packs, searchQuery],
  );

  const visibilityOptions = useMemo<readonly SegmentOption<VisibilityFilter>[]>(
    () =>
      VISIBILITY_FILTER_ORDER.map((value) => ({
        value,
        label: filterLabels.visibility[value],
        count:
          value === 'all'
            ? searchScoped.length
            : searchScoped.filter((p) => p.visibility === value).length,
      })),
    [searchScoped, filterLabels],
  );

  const tierOptions = useMemo(
    () =>
      TIER_ORDER.filter((tier) => packs.some((p) => p.tier === tier)).map((tier) => ({
        value: tier,
        label: filterLabels.tier[tier],
      })),
    [packs, filterLabels],
  );

  const kindOptions = useMemo(
    () =>
      KIND_ORDER.filter((kind) => packs.some((p) => p.kind === kind)).map((kind) => ({
        value: kind,
        label: filterLabels.kind[kind],
      })),
    [packs, filterLabels],
  );

  const verifiedOptions: readonly SegmentOption<VerifiedFilter>[] = [
    { value: 'all', label: copy.verifiedAll },
    { value: 'verified', label: copy.verifiedOnly },
  ];
  const activeOptions: readonly SegmentOption<ActiveFilter>[] = [
    { value: 'all', label: copy.activeAll },
    { value: 'active', label: copy.activeOnly },
  ];
  const sortOptions: readonly SegmentOption<CatalogSort>[] = [
    { value: 'installs', label: copy.sortInstalls },
    { value: 'name', label: copy.sortName },
    { value: 'recent', label: copy.sortRecent },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={filters.search}
          onChange={(value) => onChange({ search: value })}
          ariaLabel={copy.searchAriaLabel}
          placeholder={copy.searchPlaceholder}
        />
        <SegmentedControl
          value={filters.visibility}
          onChange={(value) => onChange({ visibility: value })}
          options={visibilityOptions}
          ariaLabel={copy.visibilityAriaLabel}
        />
        <FacetMultiSelect
          label={copy.tierLabel}
          values={filters.tiers}
          onChange={(values) => onChange({ tiers: values })}
          options={tierOptions}
        />
        <FacetMultiSelect
          label={copy.kindLabel}
          values={filters.kinds}
          onChange={(values) => onChange({ kinds: values })}
          options={kindOptions}
        />
        <SegmentedControl
          value={filters.verified}
          onChange={(value) => onChange({ verified: value })}
          options={verifiedOptions}
          ariaLabel={copy.verifiedAriaLabel}
        />
        <SegmentedControl
          value={filters.active}
          onChange={(value) => onChange({ active: value })}
          options={activeOptions}
          ariaLabel={copy.activeAriaLabel}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {totalCount > 0 && (
          <span className="text-sm tabular-nums text-muted-foreground">
            {copy.resultCount(resultCount, totalCount)}
          </span>
        )}
        <SegmentedControl
          value={filters.sort}
          onChange={(value) => onChange({ sort: value })}
          options={sortOptions}
          ariaLabel={copy.sortAriaLabel}
        />
      </div>
    </div>
  );
}
